import { Operator } from '@ardoq/api-types';
import {
  getDateOperatorToLabel,
  getDefaultOperatorToLabel,
} from '@ardoq/query-builder';
import type {
  DashboardFilterOperatorButtonProps,
  DashboardFilterPillPopoverContentProps,
  DashboardFilterPillsUIStateWithSetters,
  DashboardFilterPillValueProps,
} from '@ardoq/table-filter-bar';
import type { Locale } from '@ardoq/locale';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { DateDashboardFilter } from '../../types';
import { formatDateISO, formatDateShort, startOfToday } from '@ardoq/date-time';
import { isRelativeDate } from '@ardoq/date-time-picker';
import { DropdownMaxHeight } from '@ardoq/dropdown-2026';
import { toValuePopoverContentProps } from '../toValuePopoverContentProps';
import { type GetOperatorAndValuePropsParams, getValueProps } from './utils';

const dateOperators: Operator[] = [
  Operator.EQUAL,
  Operator.GREATER,
  Operator.LESS,
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
];

type RelativeDateOption = {
  i18nKey: string;
  defaultLabel: string;
  value: string;
};

const RELATIVE_DATE_OPTIONS: RelativeDateOption[] = [
  {
    i18nKey: 'dashboard:filter.datePopover.today',
    defaultLabel: 'Today',
    value: 'now+0d',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneDayAgo',
    defaultLabel: 'One day ago',
    value: 'now-1d',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneMonthAgo',
    defaultLabel: 'One month ago',
    value: 'now-1m',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneYearAgo',
    defaultLabel: 'One year ago',
    value: 'now-1y',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneDayFromNow',
    defaultLabel: 'One day from now',
    value: 'now+1d',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneMonthFromNow',
    defaultLabel: 'One month from now',
    value: 'now+1m',
  },
  {
    i18nKey: 'dashboard:filter.datePopover.oneYearFromNow',
    defaultLabel: 'One year from now',
    value: 'now+1y',
  },
];

const getTranslatedRelativeDateOptions = (i18nLocale: I18nLocale) =>
  RELATIVE_DATE_OPTIONS.map(({ i18nKey, defaultLabel, value }) => ({
    label: t(i18nLocale, i18nKey, defaultLabel),
    value,
  }));

const getDateOperatorLabel = (operator: Operator, i18nLocale: I18nLocale) =>
  getDateOperatorToLabel(i18nLocale)[operator] ||
  getDefaultOperatorToLabel(i18nLocale)[operator];

const getDateValueLabel = (
  dateValue: string,
  locale: Locale,
  i18nLocale: I18nLocale
): string => {
  if (isRelativeDate(dateValue)) {
    const option = getTranslatedRelativeDateOptions(i18nLocale).find(
      opt => opt.value === dateValue
    );
    return option?.label ?? '';
  }
  return formatDateShort(dateValue, locale);
};

const getDateFilterContent = (
  filterPillUIState: DashboardFilterPillsUIStateWithSetters,
  dashboardFilter: DateDashboardFilter,
  selectedValue: string | null,
  locale: Locale,
  i18nLocale: I18nLocale,
  onValueChange: (value: string | null) => void
): DashboardFilterPillPopoverContentProps => {
  if (filterPillUIState.showDatePicker) {
    return {
      type: 'date',
      value: selectedValue,
      label: dashboardFilter.name,
      updateDashboardFilterValue: onValueChange,
      locale,
    };
  }

  const translatedOptions = getTranslatedRelativeDateOptions(i18nLocale);

  return {
    type: 'list',
    maxHeight: DropdownMaxHeight.AUTO,
    groups: [
      {
        options: [
          {
            id: 'select date',
            label: t(
              i18nLocale,
              'dashboard:filter.datePopover.selectDate',
              'Select date...'
            ),
            icon: null,
            rightIcon: null,
            count: null,
            isChecked: false,
            hideCheckbox: true,
            onClick: () =>
              requestAnimationFrame(filterPillUIState.toggleShowDatePicker), // this needs to be async to avoid the popover closing immediately after opening because of event propagation
            onCheckboxClick: filterPillUIState.toggleShowDatePicker,
          },
          ...translatedOptions.map(option => ({
            id: option.value,
            icon: null,
            rightIcon: null,
            label: option.label,
            count: null,
            isChecked: false,
            hideCheckbox: true,
            onClick: () => onValueChange(option.value),
            onCheckboxClick: () => onValueChange(option.value),
          })),
        ],
      },
    ],
  };
};

export const getDateFilterOperatorAndValueProps = ({
  filterPillUIState,
  dashboardFilter,
  updateDashboardFilterValues,
  hasInaccessibleWidgets,
  locale,
  i18nLocale,
}: GetOperatorAndValuePropsParams & {
  dashboardFilter: DateDashboardFilter;
}): {
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
} => {
  const operatorUIStateProps = {
    button: {
      isPopoverOpen: filterPillUIState.isOperatorPopoverOpen,
      onClick: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
    popover: {
      isOpen: filterPillUIState.isOperatorPopoverOpen,
      onClose: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
  };

  const filterType = dashboardFilter.type;
  const selectedValue = dashboardFilter.value;
  const operatorProps = {
    button: {
      label: getDateOperatorLabel(dashboardFilter.operator, i18nLocale),
      ...operatorUIStateProps.button,
    },
    popover: {
      ...operatorUIStateProps.popover,
      options: dateOperators.map(operator => ({
        value: operator,
        label: getDateOperatorLabel(operator, i18nLocale),
        onClick: () => {
          const value =
            operator === Operator.IS_EMPTY || operator === Operator.IS_NOT_EMPTY
              ? null
              : (selectedValue ?? formatDateISO(startOfToday()));

          filterPillUIState.toggleIsOperatorPopoverOpen();
          if (
            operator === Operator.IS_EMPTY ||
            operator === Operator.IS_NOT_EMPTY
          ) {
            filterPillUIState.setPopoverInputValue(null); // reset input value when operator doesn't require a value
          }
          updateDashboardFilterValues(dashboardFilter.id, filterType, [
            {
              operator,
              value,
            },
          ]);
        },
      })),
    },
  };
  const onValueChange = (value: string | null) => {
    updateDashboardFilterValues(
      dashboardFilter.id,
      filterType,
      value
        ? [
            {
              value,
              operator: dashboardFilter.operator ?? Operator.EQUAL,
            },
          ]
        : []
    );
    filterPillUIState.toggleIsValuePopoverOpen();
    if (filterPillUIState.showDatePicker) {
      filterPillUIState.toggleShowDatePicker();
    }
  };
  const valueProps = getValueProps({
    onClose: filterPillUIState.showDatePicker
      ? () => {
          filterPillUIState.toggleShowDatePicker();
          filterPillUIState.toggleIsValuePopoverOpen();
        }
      : filterPillUIState.toggleIsValuePopoverOpen,
    onClick: filterPillUIState.toggleIsValuePopoverOpen,
    filterPillUIState,
    i18nLocale,
    name: dashboardFilter.name,
    labelsForSelectedValues: selectedValue
      ? [getDateValueLabel(selectedValue, locale, i18nLocale)]
      : [],
    content: toValuePopoverContentProps(
      getDateFilterContent(
        filterPillUIState,
        dashboardFilter,
        selectedValue,
        locale,
        i18nLocale,
        onValueChange
      ),
      filterPillUIState,
      i18nLocale
    ),
    hasLinkedWidgets: dashboardFilter.linkCounts.total > 0,
    isInvalid: false,
    hasInaccessibleWidgets,
    clearFilter: () =>
      updateDashboardFilterValues(dashboardFilter.id, filterType, []),
  });
  return { operatorProps, valueProps };
};
